import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageLayout from '../components/LegalPageLayout';
import { SKOOLWEB_SOFTWARE_URL, CONTACT_PHONE, CONTACT_PHONE_TEL } from '../utils/siteUrls';

const RefundCancellationPolicy = () => (
  <LegalPageLayout title="Refund & Cancellation Policy">
    <section className="legal-section">
      <p>
        This Refund &amp; Cancellation Policy (&quot;Policy&quot;) explains how cancellations, refunds, and
        subscription changes work for the SKOOLWEB school management platform provided by Futuristic5 IT
        Solutions LLP (&quot;SKOOLWEB,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;).
      </p>
      <p>
        This Policy applies to platform subscriptions paid by schools and educational institutions to use
        SKOOLWEB. It does not apply to fees that schools collect from students, parents, or staff inside
        the platform.
      </p>
      <p>
        This Policy supplements our{' '}
        <Link to="/terms-and-conditions">Terms &amp; Conditions</Link> and{' '}
        <Link to="/privacy-policy">Privacy Policy</Link>. If there is a conflict regarding billing,
        refunds, or cancellations, this Policy will prevail.
      </p>
      <p>By subscribing to or paying for SKOOLWEB, you agree to this Policy.</p>
    </section>

    <section className="legal-section">
      <h2>1. Definitions</h2>
      <ul>
        <li>
          <strong>Customer / Subscriber:</strong> A school, college, institute, or authorized organization
          that registers for SKOOLWEB.
        </li>
        <li>
          <strong>Subscription:</strong> Paid or trial access to SKOOLWEB modules for a defined period.
        </li>
        <li>
          <strong>Billing Period:</strong> The duration covered by a subscription payment (e.g., monthly,
          quarterly, or yearly).
        </li>
        <li>
          <strong>Trial Period:</strong> A free evaluation period before a paid subscription begins.
        </li>
        <li>
          <strong>Setup Fee:</strong> A one-time fee for account setup, onboarding, configuration, or
          training.
        </li>
        <li>
          <strong>Grace Period:</strong> A short period after subscription expiry during which limited access
          or renewal may still be possible.
        </li>
        <li>
          <strong>Verified Payment:</strong> A payment that SKOOLWEB has reviewed and approved after
          validating transaction details and payment proof.
        </li>
      </ul>
    </section>

    <section className="legal-section">
      <h2>2. Subscription Plans</h2>
      <p>
        SKOOLWEB offers subscription plans as described on our{' '}
        <Link to="/pricing">Pricing</Link> page. The price, billing interval, included modules,
        setup fee, and trial period for each plan are the ones published there.
      </p>
      <p>
        Complimentary or zero-price plans, where offered, may be subject to usage limits such as a
        maximum number of students and the modules included in that plan.
      </p>
      <p>
        Paid plans may include a subscription fee, a one-time setup fee, and a trial period. Those
        details are shown on the Pricing page and may change for new subscriptions.
      </p>
      <p>
        All prices are in Indian Rupees (INR) unless stated otherwise. Applicable taxes, including GST, may
        be charged separately unless explicitly included.
      </p>
      <p>
        We may change pricing for new subscriptions in the future. Any active paid subscription will remain
        valid for the period already paid for.
      </p>
      <p>
        Custom, enterprise, or complimentary plans may be governed by a separate written agreement.
      </p>
    </section>

    <section className="legal-section">
      <h2>3. How Payments Work</h2>
      <p>SKOOLWEB subscription payments are processed as follows:</p>
      <ul>
        <li>The school selects a plan and billing period.</li>
        <li>
          Payment is made through approved methods such as bank transfer or UPI, using account details
          provided by SKOOLWEB.
        </li>
        <li>
          The school submits payment details through the platform or as instructed by our team, including
          amount, transaction ID, and payment proof where required.
        </li>
        <li>Only one pending payment may be under review at a time.</li>
        <li>SKOOLWEB verifies the payment, typically within 3 to 5 business days.</li>
        <li>The subscription is activated or extended only after payment verification.</li>
        <li>If a payment is rejected, the school will be informed and may submit corrected details.</li>
      </ul>
      <p>
        The school is responsible for providing accurate payment information, the correct amount, and valid
        proof of payment. SKOOLWEB is not responsible for delays caused by incorrect details, bank
        processing times, or third-party payment failures.
      </p>
    </section>

    <section className="legal-section">
      <h2>4. Trial Period</h2>
      <p>Where a trial is offered:</p>
      <ul>
        <li>The trial period begins when the trial subscription is activated.</li>
        <li>
          No subscription fee is charged during the trial unless a setup fee or other charge was agreed
          separately in writing.
        </li>
        <li>During the trial, access is provided according to the selected plan features.</li>
        <li>At the end of the trial, the subscription will require payment to continue uninterrupted access.</li>
        <li>If payment is not received, access may be restricted after a grace period of 7 days.</li>
        <li>Trials are generally limited to one trial per school or institute.</li>
        <li>No refund applies to a trial where no payment was collected.</li>
      </ul>
    </section>

    <section className="legal-section">
      <h2>5. Cancellation Policy</h2>
      <h3>5.1 Who Can Cancel</h3>
      <p>
        Only authorized representatives of the subscribing school, such as the headmaster, administrator,
        or designated billing contact, may request cancellation.
      </p>
      <h3>5.2 How to Request Cancellation</h3>
      <p>To cancel a subscription, contact us at:</p>
      <ul>
        <li>
          <strong>Email:</strong>{' '}
          <a href="mailto:billing@skoolweb.com">billing@skoolweb.com</a>
        </li>
        <li>
          <strong>Phone:</strong>{' '}
          <a href={`tel:${CONTACT_PHONE_TEL}`}>{CONTACT_PHONE}</a>
        </li>
        <li>
          <strong>Subject:</strong> Cancellation Request – [School Name]
        </li>
      </ul>
      <p>Please include:</p>
      <ul>
        <li>School / institute name</li>
        <li>Registered email address</li>
        <li>Plan name</li>
        <li>Reason for cancellation (optional)</li>
      </ul>
      <p>
        You may also submit a request through our <Link to="/contact">Contact Us</Link> page.
      </p>
      <h3>5.3 Types of Cancellation</h3>
      <p><strong>Cancellation at Period End</strong></p>
      <p>
        Access continues until the end of the current paid billing period. No further renewal charges apply
        after that date.
      </p>
      <p><strong>Immediate Cancellation</strong></p>
      <p>
        Access may be stopped immediately upon request or upon non-payment. Fees already paid for the
        current billing period are generally not refunded.
      </p>
      <p><strong>Automatic Cancellation or Expiry</strong></p>
      <p>
        If payment is not received and the grace period expires, the subscription may automatically expire
        or become inactive.
      </p>
      <h3>5.4 Effect of Cancellation</h3>
      <p>After cancellation or expiry:</p>
      <ul>
        <li>Access to paid modules and features may be restricted or removed</li>
        <li>The school remains responsible for any outstanding invoices or unpaid fees</li>
        <li>Setup fees are not refunded if onboarding has already started</li>
        <li>The school should export any required data before cancellation where possible</li>
        <li>SKOOLWEB may retain billing records, invoices, and audit logs as required by law</li>
      </ul>
      <h3>5.5 Reactivation</h3>
      <p>
        A cancelled or expired account may be reactivated by submitting a new payment and requesting
        reactivation. Data may be restored only if it is still within our data retention period.
      </p>
    </section>

    <section className="legal-section">
      <h2>6. Refund Policy</h2>
      <h3>6.1 General Rule</h3>
      <p>
        Subscription fees are non-refundable once the subscription period has been activated and access has
        been granted, except where required by applicable law or expressly stated in this Policy.
      </p>
      <h3>6.2 When a Refund May Be Approved</h3>
      <p>SKOOLWEB may approve a refund in the following cases:</p>
      <ul>
        <li>Duplicate payment for the same billing period</li>
        <li>Payment of an amount higher than the invoiced or agreed amount</li>
        <li>Verified payment applied in error without corresponding service activation</li>
        <li>Other exceptional cases approved in writing by SKOOLWEB</li>
      </ul>
      <h3>6.3 When Refunds Are Not Provided</h3>
      <p>Refunds will generally not be provided for:</p>
      <ul>
        <li>Change of mind after subscription activation</li>
        <li>Unused time remaining in the current billing period</li>
        <li>Failure to use the platform</li>
        <li>Mid-term cancellation</li>
        <li>Setup or onboarding fees once onboarding has started</li>
        <li>Rejected or unverified payments</li>
        <li>Trial periods where no charge was collected</li>
        <li>Fees collected by the school from students or parents within SKOOLWEB</li>
        <li>
          Suspension or termination due to violation of our Terms &amp; Conditions, misuse, fraud, or
          non-payment
        </li>
      </ul>
      <h3>6.4 Setup Fee</h3>
      <p>
        The setup fee covers services such as account creation, initial configuration, onboarding, and
        basic training.
      </p>
      <p>The setup fee is non-refundable once onboarding or setup work has begun.</p>
      <p>
        A setup fee may be refunded only if SKOOLWEB fails to provision the account within a reasonable
        time and no onboarding work has started.
      </p>
      <h3>6.5 Refund Method and Timeline</h3>
      <ul>
        <li>Approved refunds will be processed to the original payment source wherever possible.</li>
        <li>Refund processing typically takes 7 to 15 business days after approval.</li>
        <li>Refund approval depends on verification of payment records and transaction details.</li>
      </ul>
    </section>

    <section className="legal-section">
      <h2>7. Plan Upgrades and Downgrades</h2>
      <h3>Upgrades</h3>
      <p>
        If a school upgrades to a higher plan, the upgrade may take effect immediately or at the start of
        the next billing cycle, depending on the arrangement. Additional fees may apply.
      </p>
      <h3>Downgrades</h3>
      <p>
        If a school downgrades to a lower plan, the downgrade generally takes effect at the end of the
        current billing period.
      </p>
      <p>
        No refund or credit is provided for the unused portion of the current billing period unless
        explicitly agreed in writing.
      </p>
      <p>Module access will change according to the new plan.</p>
    </section>

    <section className="legal-section">
      <h2>8. Free Plan</h2>
      <ul>
        <li>
          The Free Plan is offered at SKOOLWEB&apos;s discretion and may include limits on students, modules,
          or features.
        </li>
        <li>No payment is required for the Free Plan, and therefore no refund applies.</li>
        <li>SKOOLWEB may modify or discontinue the Free Plan with reasonable notice where possible.</li>
        <li>Schools may upgrade to a paid plan at any time.</li>
      </ul>
    </section>

    <section className="legal-section">
      <h2>9. Data Retention After Cancellation</h2>
      <p>After cancellation or expiry:</p>
      <ul>
        <li>School data may be retained for up to 90 days</li>
        <li>During this period, the school may request data export by contacting us</li>
        <li>After the retention period, data may be permanently deleted from active systems</li>
        <li>Backup copies may be retained for a limited period for disaster recovery and legal compliance</li>
      </ul>
      <p>
        Schools are responsible for exporting their data before cancellation if they wish to retain local
        copies.
      </p>
    </section>

    <section className="legal-section">
      <h2>10. Invoices and Taxes</h2>
      <ul>
        <li>Invoices are issued after verified payment, where applicable.</li>
        <li>
          Schools providing a valid GSTIN may receive tax invoices in accordance with applicable Indian tax
          laws.
        </li>
        <li>Refunds, if approved, will be processed in compliance with applicable tax regulations.</li>
        <li>Any invoice dispute must be raised within 15 days of the invoice date.</li>
      </ul>
    </section>

    <section className="legal-section">
      <h2>11. Disputes and Chargebacks</h2>
      <p>
        If you believe a payment was made in error, please contact SKOOLWEB before initiating a bank
        chargeback or payment dispute.
      </p>
      <p>
        Unauthorized chargebacks without prior notice may result in suspension of the account while the
        matter is reviewed.
      </p>
    </section>

    <section className="legal-section">
      <h2>12. Changes to This Policy</h2>
      <p>
        We may update this Policy from time to time. The updated version will be posted on this page with a
        revised &quot;Last updated&quot; date.
      </p>
      <p>
        Material changes may also be communicated to registered school administrators by email or
        in-platform notification.
      </p>
      <p>
        Continued use of SKOOLWEB after changes take effect constitutes acceptance of the updated Policy.
      </p>
    </section>

    <section className="legal-section">
      <h2>Contact Information</h2>
      <ul className="legal-contact-list">
        <li>
          <strong>Legal Email:</strong>{' '}
          <a href="mailto:hello@futuristic5.com">hello@futuristic5.com</a>
        </li>
        <li>
          <strong>Phone:</strong>{' '}
          <a href={`tel:${CONTACT_PHONE_TEL}`}>{CONTACT_PHONE}</a>
        </li>
        <li>
          <strong>Website:</strong> <Link to="/contact">Contact Us</Link>
        </li>
        <li>
          <strong>Product:</strong>{' '}
          <a href={SKOOLWEB_SOFTWARE_URL} target="_blank" rel="noopener noreferrer">
            SKOOLWEB School Management Platform
          </a>
        </li>
        <li><strong>Company:</strong> Futuristic5 IT Solutions LLP</li>
      </ul>
    </section>
  </LegalPageLayout>
);

export default RefundCancellationPolicy;
